'use client'

import { useState, useEffect } from 'react'
import { PaymentWithDetails } from '@/types'
import { useCurrency } from '@/lib/currency'
import { getApiUrl } from '@/lib/apiUrl'
import { commissionApi } from '@/lib/api'
import PaymentReceipt from '@/components/client/PaymentReceipt'

interface TransactionWithDetails extends PaymentWithDetails {
  // Additional transaction-specific properties
  isEscrow?: boolean
  escrowStatus?: 'held' | 'released' | 'expired' | 'cancelled'
}

export default function TransactionManagement() {
  const [transactions, setTransactions] = useState<TransactionWithDetails[]>([])
  const { format } = useCurrency()
  // const [payments, setPayments] = useState<Payment[]>([]) // Unused
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'completed' | 'failed'>('all')
  const [totalRevenue, setTotalRevenue] = useState(0)
  const [totalCommission, setTotalCommission] = useState(0)
  const [pendingAmount, setPendingAmount] = useState(0)
  const [commissionRate, setCommissionRate] = useState<number>(0.10) // Default 10%, loaded from API

  // Load commission rate
  const loadCommissionRate = async () => {
    try {
      const token = localStorage.getItem('authToken')
      if (!token) return
      
      const response = await commissionApi.getRate(token)
      if (response.success && response.data) {
        setCommissionRate((response.data as any).commissionRate || 0.10)
      }
    } catch (error) {
      console.error('Error loading commission rate:', error)
    }
  }

  // Load all payments (transactions are now stored in payments table)
  const loadData = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        console.error('No auth token found')
        setLoading(false)
        return
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/payments/admin/all`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      })

      if (!response.ok) {
        throw new Error(`Failed to load transactions: ${response.statusText}`)
      }

      const data = await response.json()
      const paymentsData = data.payments || []

      // Transform backend payments to frontend format
      const transformedPayments = paymentsData.map((p: any) => ({
        ...p,
        id: p._id || p.id,
        application_id: p.application?._id || p.application || p.applicationId,
        client_id: p.user?._id || p.user || p.clientId,
        landlord_id: p.application?.landlord?._id || p.application?.landlord || p.landlordId,
        property_id: p.application?.property?._id || p.application?.property || p.propertyId || '',
        created_at: p.createdAt || p.created_at,
        updated_at: p.updatedAt || p.updated_at,
        stripe_payment_intent_id: p.stripePaymentIntentId || p.stripe_payment_intent_id,
        commission_amount: p.commission_amount || 0,
        commission_rate: p.commission_rate || 0,
        landlord_amount: p.amount - (p.commission_amount || 0),
        isEscrow: p.isEscrow,
        escrowStatus: p.escrowStatus,
        escrowHeldAt: p.escrowHeldAt,
        escrowExpiresAt: p.escrowExpiresAt,
        escrowReleasedAt: p.escrowReleasedAt,
        propertyVisited: p.propertyVisited,
        documentsReceived: p.documentsReceived,
        application: p.application
      }))

      // setPayments(transformedPayments) // Unused - using setTransactions instead
      setTransactions(transformedPayments)

      // Calculate totals from payments
      const completedPayments = transformedPayments.filter((p: any) => p.status === 'completed')
      const pendingPayments = transformedPayments.filter((p: any) => p.status === 'pending')
      
      // Total revenue is sum of all completed payment amounts
      setTotalRevenue(completedPayments.reduce((sum: number, p: any) => sum + p.amount, 0))
      // Commission is only from released escrow payments (commission is calculated on release)
      setTotalCommission(completedPayments
        .filter((p: any) => p.escrowStatus === 'released' && p.commission_amount)
        .reduce((sum: number, p: any) => sum + (p.commission_amount || 0), 0))
      setPendingAmount(pendingPayments.reduce((sum: number, p: any) => sum + p.amount, 0))

    } catch (error) {
      console.error('Error loading data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadCommissionRate()
    loadData()
  }, [])

  // Format currency via currency context
  const formatCurrency = (amount: number) => format(amount)

  // Get status badge color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // Filter transactions
  const filteredTransactions = transactions.filter(transaction => {
    if (filter === 'all') return true
    return transaction.status === filter
  })

  // Calculate commission rate from actual data
  // Note: commissionRate is now loaded from API, see loadCommissionRate() function

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900">Transaction Management</h2>
        <div className="flex space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Transactions</option>
            <option value="pending">Pending</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Total Revenue</h3>
          <p className="text-3xl font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
          <p className="text-sm text-gray-500">From completed transactions</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Platform Commission</h3>
          <p className="text-3xl font-bold text-blue-600">{formatCurrency(totalCommission)}</p>
          <p className="text-sm text-gray-500">{(commissionRate * 100).toFixed(1)}% commission rate</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Pending Amount</h3>
          <p className="text-3xl font-bold text-yellow-600">{formatCurrency(pendingAmount)}</p>
          <p className="text-sm text-gray-500">Awaiting confirmation</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-2">Total Transactions</h3>
          <p className="text-3xl font-bold text-purple-600">{transactions.length}</p>
          <p className="text-sm text-gray-500">All time</p>
        </div>
      </div>

      {/* Transaction List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Transaction History</h3>
        </div>
        
        {loading ? (
          <div className="p-6 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-600">Loading transactions...</p>
          </div>
        ) : filteredTransactions.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Transaction ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Property / Client
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commission ({(commissionRate * 100).toFixed(1)}%)
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Escrow Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredTransactions.map((transaction) => (
                  <tr key={transaction.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {String(transaction.id).slice(0, 8)}...
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      <div>
                        <div className="font-medium">
                          {transaction.application?.property?.title || 'N/A'}
                        </div>
                        <div className="text-xs text-gray-500">
                          {transaction.application?.client?.firstName && transaction.application?.client?.lastName
                            ? `${transaction.application.client.firstName} ${transaction.application.client.lastName}`
                            : transaction.application?.client?.email || 'Unknown Client'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {formatCurrency(transaction.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                      {transaction.escrowStatus === 'released' && transaction.commission_amount
                        ? formatCurrency(transaction.commission_amount)
                        : transaction.isEscrow && transaction.escrowStatus === 'held'
                          ? <span className="text-gray-400">Pending release</span>
                          : transaction.commission_amount
                            ? formatCurrency(transaction.commission_amount)
                            : <span className="text-gray-400">₦0</span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {transaction.isEscrow ? (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          transaction.escrowStatus === 'held' ? 'bg-yellow-100 text-yellow-800' :
                          transaction.escrowStatus === 'released' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {transaction.escrowStatus === 'held' ? '💰 Held' :
                           transaction.escrowStatus === 'released' ? '✅ Released' :
                           transaction.escrowStatus || 'N/A'}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">Not in escrow</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(transaction.status)}`}>
                        {transaction.status.charAt(0).toUpperCase() + transaction.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex flex-col gap-2">
                        {transaction.isEscrow && transaction.escrowStatus === 'held' && (
                          <button
                            onClick={async () => {
                              if (!confirm(`Release escrow to landlord? This will deduct ${(commissionRate * 100).toFixed(1)}% commission.`)) return
                              try {
                                const token = localStorage.getItem('authToken')
                                if (!token) {
                                  alert('Please log in')
                                  return
                                }
                                const apiUrl = getApiUrl()
                                const response = await fetch(`${apiUrl}/payments/${transaction.id}/escrow/release`, {
                                  method: 'PUT',
                                  headers: {
                                    'Authorization': `Bearer ${token}`,
                                    'Content-Type': 'application/json',
                                  }
                                })
                                if (response.ok) {
                                  alert(`Escrow released successfully! ${(commissionRate * 100).toFixed(1)}% commission deducted.`)
                                  loadData()
                                } else {
                                  const error = await response.json()
                                  alert(error.message || 'Failed to release escrow')
                                }
                              } catch (error) {
                                console.error('Error releasing escrow:', error)
                                alert('Failed to release escrow')
                              }
                            }}
                            className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                            title="Release escrow after client visits property"
                          >
                            Release Escrow
                          </button>
                        )}
                        {transaction.isEscrow && transaction.escrowStatus === 'released' && (
                          <span className="text-xs text-green-600">✓ Released</span>
                        )}
                        {transaction.status === 'completed' && (
                          <PaymentReceipt 
                            paymentId={transaction.id}
                            buttonText="View Receipt"
                            buttonClassName="px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                          />
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {transaction.created_at ? new Date(transaction.created_at).toLocaleDateString() : 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p>No transactions found</p>
            <p className="text-sm">Transactions will appear here when payments are processed</p>
          </div>
        )}
      </div>

      {/* Commission Summary */}
      <div className="bg-blue-50 rounded-lg p-6">
        <h3 className="text-lg font-medium text-blue-900 mb-4">Commission Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-500">Total Commission</h4>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(totalCommission)}</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-500">Commission Rate</h4>
            <p className="text-2xl font-bold text-green-600">{(commissionRate * 100).toFixed(1)}%</p>
          </div>
          <div className="bg-white rounded-lg p-4">
            <h4 className="text-sm font-medium text-gray-500">Average Commission</h4>
            <p className="text-2xl font-bold text-purple-600">
              {transactions.length > 0 ? formatCurrency(totalCommission / transactions.length) : formatCurrency(0)}
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
